import { Map as ImmutableMap, List } from 'immutable';
import { DateTime } from 'luxon';
import { v4 as uuidv4 } from 'uuid';

import store from 'app-components/utils/redux-store';
import type RootState from 'app-components/utils/redux-store/rootState';

import { getLogger } from '../../utils/core';
import { accountsSelectors, accountsUtils } from '../accounts';
import { categoriesSelectors } from '../categories';
import { chartOfAccountsTypes, chartOfAccountsUtils } from '../chart-of-accounts';
import { dateSelectors } from '../date';
import { preferencesV2Selectors } from '../preferences-v2';
import { scheduledTransactionsSelectors } from '../scheduled-transactions';
import { SCHEDULED_TRANSACTIONS_REDUCER_KEY } from '../scheduled-transactions/scheduledTransactionsTypes';
import { splitsHelpers, transactionsSelectors, transactionsTypes, transactionsUtils } from '../transactions';
import {
  isAccountsReceivableCreditTxn,
  isInvoicePaymentSplit,
  isInvoicePaymentTransfer,
  isQBPCreditTxn,
} from '../transactions/transactionsSelectors';
import {
  mkCashFlowTransactionPatch,
  type SplitItem,
  type TransactionProps,
  type TransactionRecord,
  TransactionSubType,
} from '../transactions/transactionsTypes';
import { isTxnMatch, mergeTxns } from '../transactions/transactionsUtils';
import { Guide } from './transactionsProcessingTypes';
import { guideFilter } from './transactionsProcessingUtils';

const {
  isSplitTxn,
  findMatchingTransferTx,
  isTransferTxn,
  isBankDownloadedTxn,
  setReportsFlags,
  isAutoExcludedFromF2S,
  isMultiCurrencyTransferTxn,
  isUnsyncedTransferTxn,
  isSupportedTransferTxnWithFlags,
  txnHasUnsupportedTransfer,
  isPendingTxn,
  addPossibleMatch,
  findTransaction,
  findPossibleTransferCandidate,
  possiblyMatchTransfer,
  areTagsEqual,
  coasAreDifferent,
  fieldChanged,
  updateNewTags,
  isExistingTxn,
  isSupportedTransactionAmount,
} = transactionsUtils;

type txnMap = ImmutableMap<string, TransactionRecord>;
type accTxnMap = ImmutableMap<string, ImmutableMap<string, TransactionRecord>>;
export type MatchObject = {
  possibleMatch: TransactionRecord;
  baseTxn: TransactionRecord;
  forceWarn: boolean;
};
export type TxnPack = {
  allTxnsToUpdate: TransactionRecord[];
  allTxnsToDelete: TransactionRecord[];
  allTxnsToCreate: TransactionRecord[];
  allMatches: MatchObject[];
  warnings: string[];
  errors: string[];
  guides: Guide[];
  extraActions: any[];
};

const log = getLogger('flux/transactions/preProcess.js');

function isPersonalBusiness(businessId: string | null | undefined): boolean {
  return !businessId || businessId === '0' || businessId === 'PERSONAL';
}

// Use the account business when a transfer side does not specify one. Personal sides have no
// customer or project, and existing business counterparts retain their own values.
function resolveTransferBusinessFields(
  transferAccountId: string,
  requestedBusinessId: string | null | undefined,
  requestedCustomerId: string | null | undefined,
  requestedProjectId: string | null | undefined,
  state: RootState,
): { businessId: string; customerId: string | null | undefined; projectId: string | null | undefined } {
  const transferAccount = accountsSelectors.getAccountById(state, transferAccountId);
  const businessId = requestedBusinessId ?? transferAccount?.businessId ?? '';
  const isPersonal = isPersonalBusiness(businessId);
  return {
    businessId,
    customerId: isPersonal ? '' : requestedCustomerId,
    projectId: isPersonal ? '' : requestedProjectId,
  };
}

type TransferBusinessFields = Pick<TransactionProps, 'businessId' | 'customerId' | 'projectId'>;

function resolveExistingTransferBusinessFields(txn: TransferBusinessFields): TransferBusinessFields {
  const isPersonal = isPersonalBusiness(txn.businessId);
  return {
    businessId: txn.businessId,
    customerId: isPersonal ? '' : txn.customerId,
    projectId: isPersonal ? '' : txn.projectId,
  };
}

function resolveNewTransferBusinessFields(
  accountId: string,
  requested: TransferBusinessFields | undefined,
  source: TransferBusinessFields,
  state: RootState,
): { businessId: string; customerId: string; projectId: string } {
  const fields = resolveTransferBusinessFields(
    accountId,
    requested?.businessId,
    requested?.customerId,
    requested?.projectId,
    state,
  );
  const canCopy = !isPersonalBusiness(fields.businessId) && fields.businessId === source.businessId;
  return {
    businessId: fields.businessId,
    customerId: fields.customerId ?? (canCopy ? source.customerId : '') ?? '',
    projectId: fields.projectId ?? (canCopy ? source.projectId : '') ?? '',
  };
}

function pickTransferBusinessFields(txn: TransferBusinessFields): TransferBusinessFields {
  return { businessId: txn.businessId, customerId: txn.customerId, projectId: txn.projectId };
}

// Resolve the business fields for a connected counterpart. When the counterpart is also part of the
// submitted batch, its submitted values win over the stored record so both sides agree regardless of
// the order in which they are processed.
function resolveConnectedTransferBusinessFields(
  storedTxn: TransactionRecord,
  submittedTxns: readonly TransactionRecord[] | undefined,
  state: RootState,
): { fields: TransferBusinessFields; submittedCounterpart: TransactionRecord | undefined } {
  const submittedCounterpart = storedTxn.id ? submittedTxns?.find((txn) => txn.id === storedTxn.id) : undefined;
  if (submittedCounterpart) {
    return {
      submittedCounterpart,
      fields: resolveTransferBusinessFields(
        submittedCounterpart.accountId ?? storedTxn.accountId ?? '',
        submittedCounterpart.businessId,
        submittedCounterpart.customerId,
        submittedCounterpart.projectId,
        state,
      ),
    };
  }
  return { submittedCounterpart, fields: resolveExistingTransferBusinessFields(storedTxn) };
}

// Normalize the business fields of the transfer side(s) on a submitted txn once, before any
// counterpart logic runs, so every branch (match, create, already-connected) reads the same values.
function normalizeTransferBusinessFields(txn: TransactionRecord, featureFlags: any, state: RootState) {
  if (!txn.accountId) return txn;
  if (isSplitTxn(txn)) {
    const items = txn.split?.items;
    if (!items || !('map' in items)) return txn;
    return txn.setIn(
      ['split', 'items'],
      items.map((item) =>
        isSupportedTransferTxnWithFlags(item, featureFlags, state) && item.coa?.id && item.coa.id !== txn.accountId
          ? transactionsTypes
              .mkSplitItem(item)
              .merge(
                resolveTransferBusinessFields(txn.accountId!, item.businessId, item.customerId, item.projectId, state),
              )
          : item,
      ),
    );
  }
  if (isTransferTxn(txn)) {
    return txn.merge(
      resolveTransferBusinessFields(txn.accountId, txn.businessId, txn.customerId, txn.projectId, state),
    );
  }
  return txn;
}

// used to update txn buckets when necessary (allTxnsToUpdate/allTxnsToCreate)
export function replaceInArray(array: any[], itemToReplace: TransactionRecord) {
  const foundIndex = array.findIndex(
    (item) => item?.id === itemToReplace?.id || item?.clientId === itemToReplace?.clientId,
  );
  const found = foundIndex > -1;
  if (found) {
    array.splice(foundIndex, 1, itemToReplace);
  }
  return found;
}

function makeTransferTxn(txn: TransactionRecord, guid: string, overrides?: Partial<TransactionProps>) {
  return transactionsTypes.mkCashFlowTransaction({
    accountId: overrides?.accountId || txn.coa?.id,
    coa: {
      type: 'ACCOUNT',
      id: txn.accountId!,
    },
    amount: overrides?.amount || Number(txn.amount) * -1,
    split: null,
    cpData: null,
    matchState: 'NOT_MATCHED',
    transfer: {
      id: txn.id ? txn.id : undefined,
      clientId: txn.clientId, // assumes clientId already set
    },
    clientId: guid, // new GUID for transfer transaction
    businessId: overrides?.businessId ?? txn.businessId,
    customerId: overrides?.customerId ?? txn.customerId,
    projectId: overrides?.projectId ?? txn.projectId,
    tags: overrides?.tags || txn.tags,
    isExcludedFromReports: overrides?.isExcludedFromReports ?? txn.isExcludedFromReports,
    isExcludedFromF2S: overrides?.isExcludedFromF2S ?? txn.isExcludedFromF2S,
    check: null,

    payee: txn.payee,
    memo: overrides?.memo ?? txn.memo,
    postedOn: txn.postedOn,
    // for sales tax, they need to be created as cleared because they cannot be updated to CLEARED later
    state: txn.type === 'INVOICE' ? 'CLEARED' : (overrides?.state ?? txn.state),
    stModelId: txn.stModelId,
    stDueOn: txn.stDueOn,
    isBill: txn.isBill,
    isSubscription: txn.isSubscription,
  });
}

// Treats null, undefined, and '' as equivalent when comparing optional string fields
function optionalFieldChanged(a: string | null | undefined, b: string | null | undefined) {
  return (a || '') !== (b || '');
}

function shouldUpdateTransfer(oldTransfer: TransactionRecord, newTransfer: TransactionRecord) {
  return Boolean(
    Number(oldTransfer.amount) !== Number(newTransfer.amount) ||
      optionalFieldChanged(oldTransfer.businessId, newTransfer.businessId) ||
      (newTransfer.customerId !== undefined && optionalFieldChanged(oldTransfer.customerId, newTransfer.customerId)) ||
      (newTransfer.projectId !== undefined && optionalFieldChanged(oldTransfer.projectId, newTransfer.projectId)) ||
      !areTagsEqual(oldTransfer.tags, newTransfer.tags) ||
      // Patch paths leave `state` undefined to mean "no state change requested"; don't diff against it.
      (newTransfer.state !== undefined && oldTransfer.state !== newTransfer.state) ||
      !DateTime.fromISO(oldTransfer.postedOn).hasSame(DateTime.fromISO(newTransfer.postedOn), 'day'),
  );
}

// Returns 'CLEARED' for not-yet-cleared manual counterparts; `undefined` otherwise so banks own state
// and we don't demote RECONCILED.
function getManualDestinationStateOverride(
  state: RootState,
  accountId: string | undefined,
  currentState?: TransactionProps['state'] | null,
): 'CLEARED' | undefined {
  const destinationAccount = accountId ? accountsSelectors.getAccountById(state, accountId) : undefined;
  if (!destinationAccount) return undefined;
  if (!accountsUtils.isAccountManual(destinationAccount)) return undefined;
  if (currentState === 'CLEARED' || currentState === 'RECONCILED') return undefined;
  return 'CLEARED';
}

function isScheduledSplitTransferCounterpart(txn: TransactionRecord, state: RootState): boolean {
  if (!txn.stModelId || !state?.[SCHEDULED_TRANSACTIONS_REDUCER_KEY]) {
    return false;
  }
  return Boolean(
    splitsHelpers.getScheduledInstanceSplitItemForTxn({
      txn,
      transactionsById: transactionsSelectors.getResourcesById(state),
      scheduledModelTxnsById: scheduledTransactionsSelectors.getResourcesById(state),
    }),
  );
}

// ====================================================
// createRequiredTransferTxns
//
// If this is a transfer transaction (or has split children which are transfers),
// we will make the other side(s) of it.
//
export function createRequiredTransferTxns(
  txnToProcess: TransactionRecord,
  txnsByAccountId: accTxnMap,
  featureFlags: any,
  state: RootState,
  previousTxn?: TransactionRecord,
  submittedTxns?: readonly TransactionRecord[],
) {
  const allTxnsToCreate: TransactionRecord[] = [];
  const allTxnsToUpdate: TransactionRecord[] = [];
  const warnings = [];
  const allMatches: MatchObject[] = [];
  const extraActions: any[] = [];
  const transfersToPersist: TransactionRecord[] = [];

  createOrUpdate(txnToProcess, allTxnsToCreate, allTxnsToUpdate); // push the txn before applying match logic

  let baseTxn = possiblyMatchTransfer(txnToProcess, txnsByAccountId);
  const forceWarnOnMatch = baseTxn !== txnToProcess; // if found a potential match,

  // normalize business fields up-front so matches, creates, and already-connected updates all see the same txn
  baseTxn = normalizeTransferBusinessFields(baseTxn, featureFlags, state);
  if (baseTxn !== txnToProcess) {
    replaceInArray(allTxnsToUpdate, baseTxn) || replaceInArray(allTxnsToCreate, baseTxn);
  }

  if (isSplitTxn(baseTxn) && baseTxn?.split?.items) {
    // process splits
    // baseTxn represents the txn we are processing, as a split, we will need to alter any
    // transfer split items to contain the GUID connectors

    baseTxn.split.items.forEach((item, index) => {
      if (
        isSupportedTransferTxnWithFlags(item, featureFlags, state) &&
        item.coa?.id &&
        item.coa?.id !== baseTxn.accountId
      ) {
        // item was normalized above via normalizeTransferBusinessFields
        const sourceSplitFields = pickTransferBusinessFields(item);
        let alreadyConnected = false;
        let oldTxn; // the previous 'other side' of the transfer

        // check if we are editing an existing transfer
        if (previousTxn && isTransferTxn(previousTxn)) {
          // transfer txns to consider
          const transferTxns =
            txnsByAccountId.get(item.coa.id)?.filter((tx) => isTransferTxn(tx)) ||
            ImmutableMap<string, TransactionRecord>();

          if (isSplitTxn(previousTxn)) {
            const oldSplitItem = previousTxn.split?.items?.find(
              (oldItem) => oldItem.id === item.id && oldItem.coa?.id === item.coa?.id,
            );
            if (oldSplitItem) {
              // find the existing 'otherSide'
              oldTxn = findMatchingTransferTx(
                previousTxn,
                transferTxns,
                oldSplitItem.amount,
                oldSplitItem.transfer,
              )?.txn;
            }
          } else if (previousTxn.coa && item.coa.id === previousTxn.coa.id) {
            // if new split, make sure the split contains the previous transfer coa
            oldTxn = findMatchingTransferTx(previousTxn, transferTxns)?.txn;
          }
        }

        // found existing match
        if (oldTxn) {
          if (isSplitTxn(oldTxn)) {
            const oldSplitTransferItem = oldTxn.split?.items?.find(
              (oldItem) =>
                oldItem.coa?.id === baseTxn.accountId && Number(oldItem.amount) === -Number(item.amount || 0),
            );

            if (oldSplitTransferItem) {
              alreadyConnected = true;
              baseTxn = baseTxn.setIn(['split', 'items', index, 'transfer'], {
                id: oldTxn.id,
                clientId: oldSplitTransferItem.clientId,
              });
              transfersToPersist.push(oldTxn);
            }
          } else if (item.coa?.id === oldTxn.accountId && oldTxn.coa?.id === baseTxn.accountId) {
            // still connected
            alreadyConnected = true;
            // update fields
            const { fields: transferFields, submittedCounterpart } = resolveConnectedTransferBusinessFields(
              oldTxn,
              submittedTxns,
              state,
            );
            const sharedFields = {
              amount: Number(item.amount || 0) * -1,
              ...transferFields,
              postedOn: baseTxn.postedOn,
              //  isExcludedFromReports: baseTxn.isExcludedFromReports,
              //  isExcludedFromF2S: baseTxn.isExcludedFromF2S,
            };
            // When the counterpart is co-submitted, stage a patch of just the shared fields. Patches win
            // field-by-field over full records in removeConflicts regardless of submission order, so the
            // counterpart keeps its own edits (memo, tags, ...) and still receives the shared changes.
            // coa is carried (unchanged) so the staged update satisfies doesTxnMatchGuardedTransferFields
            // when the counterpart is processed afterwards.
            const updatedTransfer = submittedCounterpart
              ? mkCashFlowTransactionPatch({
                  id: oldTxn.id,
                  clientId: oldTxn.clientId,
                  coa: oldTxn.coa,
                  ...sharedFields,
                })
              : oldTxn.merge(sharedFields);
            if (shouldUpdateTransfer(oldTxn, updatedTransfer)) {
              // update fields
              log.log('updating other side of transfer', updatedTransfer);
              allTxnsToUpdate.push(updatedTransfer);
            } else {
              transfersToPersist.push(oldTxn);
            }
          }
        }

        // only match/create new transfers if we didnt find it already connected above
        if (!alreadyConnected) {
          // check for a matching transaction, if found save with GUID for later syncing
          const possibleMatch = findPossibleTransferCandidate(
            item.coa.id, // TODO: Is this returning already matched txns???
            baseTxn.postedOn,
            -1 * Number(item.amount),
            txnsByAccountId,
          );

          // If we have a possible match, save it and save the matching transfer "to create" for processing
          if (possibleMatch) {
            log.log('found possible match', possibleMatch.toJS?.());
            baseTxn = baseTxn.setIn(['split', 'items', index, 'transfer'], {
              id: possibleMatch.id,
              clientId: possibleMatch.clientId,
            });

            const matchStateOverride = getManualDestinationStateOverride(state, item.coa.id, possibleMatch.state);
            const adjustedMatch = matchStateOverride ? possibleMatch.set('state', matchStateOverride) : possibleMatch;
            addPossibleMatch(adjustedMatch, baseTxn, allMatches, forceWarnOnMatch);
          } else {
            // no possible match, balance with a manual txn
            const newGuid = uuidv4().toUpperCase(); // new GUID for transfer transaction
            const stateOverride = getManualDestinationStateOverride(state, item.coa.id);
            const splitTransferFields = resolveNewTransferBusinessFields(
              item.coa.id,
              undefined,
              sourceSplitFields,
              state,
            );
            const transferTxn = makeTransferTxn(baseTxn, newGuid, {
              accountId: item.coa.id,
              amount: item.amount * -1,
              ...splitTransferFields,
              tags: item.tags,
              memo: item.memo,
              state: stateOverride,
              // default other-side of split transfers to excluded to balance-out the numbers.
              //  If we allow configurable in-out exclusions, we may need to replace
              isExcludedFromF2S: true,
              isExcludedFromReports: true,
            });
            log.log('no possible match', '\ncreating txn:', transferTxn);

            // set the guid on both the baseTxn, and the split item
            baseTxn = baseTxn.set('transfer', { clientId: newGuid });
            baseTxn = baseTxn.setIn(['split', 'items', index, 'transfer'], { clientId: newGuid });

            allTxnsToCreate.push(transferTxn);
            // apply transfer updates to already staged txn
            if (!(replaceInArray(allTxnsToUpdate, baseTxn) || replaceInArray(allTxnsToCreate, baseTxn))) {
              // base not already in update/create array
              log.warn(
                'Did not find baseTxn already staged for update/create.',
                '\nConsider looking into transfer balancing logic',
              );
              createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);
            }
            if (
              !isInvoicePaymentTransfer(state, { txn: baseTxn }) &&
              !isInvoicePaymentSplit(state, { item }) &&
              baseTxn.subtype !== TransactionSubType.SalesReceipt
            ) {
              // warn before doing
              warnings.push(
                `We did not find a matching transaction in the account ${accountsUtils.getAccountString(
                  transferTxn.accountId,
                  state,
                )}.` + '  Click continue to create a manual transaction, or Cancel to abort',
              );
            }
            // todo: you may get here by editing a future-dated scheduled transfer reminder.
            //  Should probably talk to Ken if it is our job to balance a such a txn or not.
          }
        }
      }
    });
  } else if (
    isSupportedTransferTxnWithFlags(baseTxn, featureFlags, state) &&
    baseTxn.coa?.id &&
    baseTxn.coa.id !== baseTxn.accountId
  ) {
    let existingTransfer: { clientId?: string | null; id?: string | null } | undefined = undefined;

    // check if existing transfer is already connected to a txn
    if (previousTxn && baseTxn.coa?.id === previousTxn.coa?.id) {
      // did not change transfer account
      const transferTxns =
        txnsByAccountId.get(baseTxn.coa.id)?.filter((tx) => isTransferTxn(tx)) ||
        ImmutableMap<string, TransactionRecord>();
      const oldTxn = findMatchingTransferTx(previousTxn, transferTxns)?.txn;
      // found existing match
      if (oldTxn) {
        if (isSplitTxn(oldTxn)) {
          // if split, look for correct splitItem
          const oldItem = oldTxn.split?.items?.find((x) => x?.coa?.id === baseTxn.accountId);
          if (oldItem) {
            existingTransfer = { id: oldTxn.id, clientId: oldItem.clientId };
            // currently, don't apply any changes to the other side of the split (oldDestinationTransfers loop should prevent amount/date/tags changes currently)
            // TODO: we now have the power to allow applying changes to both sides of a split transfer.
            //  If decide to allow this, as long as we make sure to balance the split transaction after editing, it should be fine.
            //  You would of course have to handle updating in the rest of the update and remove the block
            //  in the oldDestinationTransfers loop.
          }
        } else if (
          // not split
          baseTxn.coa?.id === oldTxn.accountId &&
          oldTxn.coa?.id === baseTxn.accountId
        ) {
          // still connected
          existingTransfer = { clientId: oldTxn.clientId, id: oldTxn.id };
        }
      }
    } else if (baseTxn.transfer?.clientId && !baseTxn.transfer.id) {
      existingTransfer = baseTxn.transfer;
      log.log('clientId was provided in transfer, this should mean that the other side is also part of creation');
    } else if (
      baseTxn.transfer?.id &&
      baseTxn.coa.id &&
      txnsByAccountId.get(baseTxn.coa.id)?.has(baseTxn.transfer.id)
    ) {
      log.log('A transfer id was provided and that transaction was found in the target account');
      existingTransfer = baseTxn.transfer;
    }

    if (existingTransfer) {
      const transferTxn = existingTransfer.id
        ? txnsByAccountId.get(baseTxn.coa.id)?.get(existingTransfer.id)
        : undefined;
      if (!isSplitTxn(transferTxn)) {
        // update fields
        let nonSplitTransferFields: TransferBusinessFields;
        let counterpartSubmitted = false;
        if (transferTxn) {
          const connected = resolveConnectedTransferBusinessFields(transferTxn, submittedTxns, state);
          nonSplitTransferFields = connected.fields;
          counterpartSubmitted = Boolean(connected.submittedCounterpart);
        } else {
          const submittedCounterpart = existingTransfer.clientId
            ? submittedTxns?.find((txn) => txn.clientId === existingTransfer.clientId)
            : undefined;
          nonSplitTransferFields = resolveNewTransferBusinessFields(
            baseTxn.coa.id,
            submittedCounterpart,
            pickTransferBusinessFields(baseTxn),
            state,
          );
        }
        // Existing business counterparts own these fields independently; new or co-submitted sides keep
        // their explicit values.
        const counterpartOwnerFields =
          !transferTxn || counterpartSubmitted || isPersonalBusiness(nonSplitTransferFields.businessId)
            ? { customerId: nonSplitTransferFields.customerId, projectId: nonSplitTransferFields.projectId }
            : {};
        const updatedTransfer = mkCashFlowTransactionPatch({
          id: existingTransfer.id,
          clientId: existingTransfer.clientId,
          amount: Number(baseTxn.amount || 0) * -1,
          businessId: nonSplitTransferFields.businessId,
          ...counterpartOwnerFields,
          tags: baseTxn.tags,
          postedOn: baseTxn.postedOn,
          payee: !transferTxn?.payee ? baseTxn.payee : undefined, // We only want to override the payee if there is not already one
          //  isExcludedFromReports: baseTxn.isExcludedFromReports,
          //  isExcludedFromF2S: baseTxn.isExcludedFromF2S,
        });
        if (!transferTxn || shouldUpdateTransfer(transferTxn, updatedTransfer)) {
          // update fields
          log.log('updating other side of transfer', updatedTransfer);
          allTxnsToUpdate.push(updatedTransfer);
        } else {
          transfersToPersist.push(transferTxn);
        }
      }
    }

    // only match/create new transfers if we didnt find it already connected above
    if (!existingTransfer) {
      // check to see if there is a transaction already in the transfer account that might be
      // a target to match up with this transfer.
      const possibleMatch = findPossibleTransferCandidate(
        baseTxn.coa.id,
        baseTxn.postedOn,
        -1 * baseTxn.amount,
        txnsByAccountId,
      );

      // If we have a possible match, save it for approval
      if (possibleMatch) {
        log.log('found possible match', possibleMatch.toJS?.());

        addPossibleMatch(possibleMatch, baseTxn, allMatches, forceWarnOnMatch);
      } else {
        // no possible match, balance with a manual txn
        const newGuid = uuidv4().toUpperCase(); // New GUID for new transfer
        const nonSplitCreateFields = resolveNewTransferBusinessFields(
          baseTxn.coa.id,
          undefined,
          pickTransferBusinessFields(baseTxn),
          state,
        );
        const transferTxn = makeTransferTxn(baseTxn, newGuid, nonSplitCreateFields);
        log.log('no possible match', '\ncreating manual txn:', transferTxn);
        baseTxn = baseTxn.set('transfer', { clientId: newGuid });

        allTxnsToCreate.push(transferTxn);
        // apply transfer updates to already staged txn
        if (!(replaceInArray(allTxnsToUpdate, baseTxn) || replaceInArray(allTxnsToCreate, baseTxn))) {
          // base not already in update/create array
          log.warn(
            'Did not find baseTxn already staged for update/create.',
            '\nConsider looking into transfer balancing logic',
          );
          createOrUpdate(baseTxn, allTxnsToCreate, allTxnsToUpdate);
        }
        if (!isInvoicePaymentTransfer(state, { txn: baseTxn }) && baseTxn.subtype !== TransactionSubType.SalesReceipt) {
          // warn before doing
          warnings.push(
            `We did not find a matching transaction in the account ${accountsUtils.getAccountString(
              transferTxn.accountId,
              state,
            )}.` + '  Click continue to create a manual transaction, or Cancel to abort',
          );
        }
      }
    }
  }
  return { allTxnsToUpdate, allTxnsToCreate, allMatches, warnings, extraActions, transfersToPersist };
}

// ----------------------------------------------------
// CreateOrUpdate (txn, createArray, updateArray)
function createOrUpdate(txn: TransactionRecord, ca: TransactionRecord[], ua: TransactionRecord[]) {
  if (!isExistingTxn(txn)) {
    ca.push(txn);
  } else {
    ua.push(txn);
  }
}

// ----------------------------------------------------
// removeTransfers
//
function mergeTxnPack(txnPack: Partial<TxnPack>, packObj: TxnPack) {
  const { allMatches, allTxnsToCreate, allTxnsToUpdate, allTxnsToDelete, extraActions, warnings, errors, guides } =
    packObj;

  if (txnPack.allTxnsToCreate) {
    txnPack.allTxnsToCreate.forEach((x) => {
      allTxnsToCreate.push(x);
    });
  }
  if (txnPack.allTxnsToUpdate) {
    txnPack.allTxnsToUpdate.forEach((x) => {
      allTxnsToUpdate.push(x);
    });
  }
  if (txnPack.allTxnsToDelete) {
    txnPack.allTxnsToDelete.forEach((x) => {
      allTxnsToDelete.push(x);
    });
  }
  if (txnPack.warnings) {
    txnPack.warnings.forEach((x) => {
      warnings.push(x);
    });
  }
  if (txnPack.errors) {
    txnPack.errors.forEach((x) => {
      errors.push(x);
    });
  }
  if (txnPack.allMatches) {
    txnPack.allMatches.forEach((x) => {
      allMatches.push(x);
    });
  }
  if (txnPack.extraActions) {
    txnPack.extraActions.forEach((x) => {
      extraActions.push(x);
    });
  }
  if (txnPack.guides) {
    guides.push(...txnPack.guides);
  }
}

// TODO for all of these we need to handle MATHED/UNMATCHED and DOWNLOADED cases and do the right thing
// whatever that is
//
// ====================================================
// preProcessDelete
//
interface preprocessDeleteProps {
  txns: TransactionRecord[];
  txnsByAccountId: accTxnMap;
  isUpdate: boolean;
  skipWarnings?: boolean;
  state?: any;
}
export function preProcessDelete(options: preprocessDeleteProps) {
  const { txns, txnsByAccountId, isUpdate = false, skipWarnings = false, state } = options;

  let allTxnsToDelete: TransactionRecord[] = [];
  const allTxnsToUpdate: TransactionRecord[] = [];
  const warnings: string[] = [];
  const errors: string[] = [];
  const extraActions: any[] = [];

  const idsToDelete = new Set(txns.map((txn) => txn.id));

  txns.forEach((txn) => {
    // we can use this to create deletion side effects without deleting the original transaction
    if (!isUpdate && txn.id) {
      const txnPrev = transactionsUtils.isRefundTxn(txn)
        ? txn
        : findTransaction(txnsByAccountId, txn.id, txn.accountId!);
      if (!txnPrev) {
        return true;
      }
      allTxnsToDelete.push(txn.set('isDeleted', true));
    }
    if (isTransferTxn(txn)) {
      if (isSplitTxn(txn)) {
        // find the split item whose amount matches the amount
        txn?.split?.items?.forEach((item) => {
          // if split item is a valid transfer, and not a self-transfer
          if (item.coa && item.coa.type === 'ACCOUNT' && item.coa.id !== txn.accountId) {
            const transferTxns = getTransferTxns(txnsByAccountId, item.coa.id); // txnsByAccountId.get(item.coa.id).filter((x) => isTransferTxn(x));
            removeMatchingTransfer({
              txn,
              transferTxns,
              updateArray: allTxnsToUpdate,
              deleteArray: allTxnsToDelete,
              warnings,
              errors,
              altAmount: item.amount,
              skipWarnings,
              state,
              idsToDelete,
            });
          }
        });
      } else if (txn.coa?.id) {
        // not a split transaction, so simply delete the other side of this transaction
        const transferTxns = getTransferTxns(txnsByAccountId, txn.coa.id); // txnsByAccountId.get(txn.coa.id).filter((x) => isTransferTxn(x));
        removeMatchingTransfer({
          txn,
          transferTxns,
          updateArray: allTxnsToUpdate,
          deleteArray: allTxnsToDelete,
          warnings,
          errors,
          altAmount: null,
          skipWarnings,
          state,
          idsToDelete,
        });
      }
    }
    return true;
  });

  // only keep the first instance of each transaction to delete (based off id)
  allTxnsToDelete = allTxnsToDelete.filter((txn1, i, arr) => arr.findIndex((txn2) => txn2.id === txn1.id) === i);

  return { allTxnsToDelete, allTxnsToUpdate, warnings, errors, extraActions };
}

function getTransferTxns(txnsByAccountId: accTxnMap, id: string) {
  const acctTxns = txnsByAccountId.get(id) || ImmutableMap<string, TransactionRecord>();
  return acctTxns.filter((x) => isTransferTxn(x));
}

//----------------------------------------------------------------------------------------------------
// When the other side of a transfer is in a split, on delete we have to update that one split row
// (we make the category 'UNCATEGORIZED').
// Additionally, we consolidate 'UNCATEGORIZED' split items and clear the split if only one item remains
//
// removeMatchingTransfer
//
interface removeMatchingTransferProps {
  txn: TransactionRecord;
  transferTxns: txnMap;
  updateArray: TransactionRecord[];
  deleteArray: TransactionRecord[];
  warnings: string[];
  errors: string[];
  altAmount: number | null;
  skipWarnings: boolean;
  state: any;
  idsToDelete: Set<string | null | undefined>;
}
function removeMatchingTransfer({
  txn,
  transferTxns,
  updateArray,
  deleteArray,
  warnings,
  errors: _errors,
  altAmount = null,
  skipWarnings,
  state,
  idsToDelete,
}: removeMatchingTransferProps) {
  const bestMatch = findMatchingTransferTx(txn, transferTxns, altAmount);

  if (bestMatch?.txn) {
    let txnToUpdate = bestMatch.txn;
    if (idsToDelete.has(txnToUpdate.id)) {
      // skip remove other-side transfer because it is already scheduled for deletion
      return;
    }
    if (bestMatch.splitItem !== null) {
      // transfer is in a split

      // if deleting multiple splitItem transfers at the same time, check to see if bestMatch has already had an item deleted
      const alreadyUpdatedTxn = updateArray.find((x) => x.id === txnToUpdate.id);
      if (alreadyUpdatedTxn && alreadyUpdatedTxn?.split?.items) {
        txnToUpdate = alreadyUpdatedTxn;
      }

      let updatedTxn;
      const items = txnToUpdate.split?.items;
      if (items && (('length' in items && items.length) || ('size' in items && items.size))) {
        const bestMatchItem = 'get' in items ? items.get(bestMatch.splitItem || 0) : items[bestMatch.splitItem || 0];
        const itemIndex =
          bestMatchItem?.coa?.id === txn.accountId // validate bestMatches' category
            ? bestMatch.splitItem
            : items.findIndex((x) => x.coa?.id === txn.accountId); // fallback to manual index if bestMatch isn't reputable

        // read customer/project from the item actually being uncategorized (may differ from bestMatchItem on fallback)
        const uncatItem = 'get' in items ? items.get(itemIndex) : items[itemIndex];
        const splitItemUncatFields = resolveTransferBusinessFields(
          txnToUpdate.accountId || '',
          uncatItem?.businessId,
          uncatItem?.customerId,
          uncatItem?.projectId,
          state,
        );
        updatedTxn = splitsHelpers.consolidateUncatSplitItems(
          txnToUpdate.mergeIn(['split', 'items', itemIndex], {
            coa: { id: '0', type: 'UNCATEGORIZED' },
            transfer: null,
            ...splitItemUncatFields,
          }),
        );
      } else {
        log.warn('split items missing in removeMatchingTransfer!');
        updatedTxn = txnToUpdate.set('coa', { id: '0', type: 'UNCATEGORIZED' });
      }

      if (alreadyUpdatedTxn) {
        replaceInArray(updateArray, updatedTxn);
      } else {
        updateArray.push(updatedTxn);
      }
      const acctName = accountsUtils.getAccountString(updatedTxn.accountId, state);
      if (!skipWarnings) {
        if (txn.source === 'SCHEDULED_TRANSACTION_PENDING') {
          warnings.push(
            `You are deleting the reminder "${txn.payee}".  The other side of this reminder` +
              ` in account [${acctName}] is in a split reminder and its line item will be set to "Uncategorized"`,
          );
        } else if (!isInvoicePaymentTransfer(state, { txn })) {
          warnings.push(
            `You are deleting the transfer transaction "${txn.payee}".  The other side of this transaction` +
              ` in account [${acctName}] is in a split transaction and its line item will be set to "Uncategorized"`,
          );
        }
      }
    } else if (txnToUpdate.cpData) {
      //
      // if the matched transaction is a downloaded and/or matched transaction, do not delete it, but un-categorize it
      // Also unset the exclusion from reports
      //
      const uncatFields = resolveTransferBusinessFields(
        txnToUpdate.accountId || '',
        txnToUpdate.businessId,
        txnToUpdate.customerId,
        txnToUpdate.projectId,
        state,
      );
      const t1 = setReportsFlags(
        txnToUpdate.merge({
          coa: { id: '0', type: 'UNCATEGORIZED' },
          transfer: null,
          ...uncatFields,
        }),
        false,
      );
      updateArray.push(t1);
      const acctName = accountsUtils.getAccountString(t1.accountId, state);

      if (!skipWarnings && !isAccountsReceivableCreditTxn(state, { txn })) {
        warnings.push(
          `You are deleting the transfer transaction "${txn.payee}".  The other side of this transfer ` +
            ` in account [${acctName}] is a bank downloaded transaction and will be set to "Uncategorized", but will not be deleted`,
        );
      }
    } else {
      const acctName = accountsUtils.getAccountString(txnToUpdate.accountId, state);

      if (!skipWarnings) {
        if (txn.source === 'SCHEDULED_TRANSACTION_PENDING') {
          warnings.push(
            `You are deleting the reminder "${txn.payee}".  This will also delete the reminder` +
              ` for the other side in account [${acctName}] `,
          );
        } else if (!isAccountsReceivableCreditTxn(state, { txn })) {
          warnings.push(
            `You are deleting the transfer transaction "${txn.payee}".  This will also delete the other side` +
              ` of the transfer in account [${acctName}] `,
          );
        }
      }
      deleteArray.push(txnToUpdate.set('isDeleted', true));
    }
  }
}

// ====================================================
// preProcessUpdate
//
// Here are the rules, subject to change
//
// Premise: Transaction has a transfer category OR has a split with transfer category(ies)
// Problem: Determine updates that need to happen to the other side of the transfer
// Note: The other side of the transfer may be a transaction coa, or within a split
// More Notes: transaction can go from split to not split, or the other way around
//
// COMPLEX!!!!!!!
//
// RULES:
//
// NORMAL TRANSFER: If this is a generic transfer (was not split) tightly bound
//  - if the other side of this transfer is within a split, NO CHANGES are allowed (Mac rules).  Transaction is locked
//  - change to amount will change both sides of the transfer
//  - change to the date will change both sides of the transfer (date/amount coupling is strongest) -with warning
//  - change to payee will change both sides of the transfer
//  - change to the category (break the transfer) will delete the other side of the transfer - with warning
//  - transaction becomes split - then previous transfer is deleted, and new transfers are created if applicable
//  - all other changes will not impact the other side of the transfer
//
// SPLIT TRANSFER: This transaction had one or more transfers in it's split items
// - change to the date will change both sides of the transfer(s) - with warning
// - change to the payee will change all payee names linked to split transfer rows
// - change to a split row amount that is a transfer will impact the amount of the other side of the transfer
// - change to a split row category that will break the existing transfer will delete the other side
// - all other changes will not impact the other side of any split row transfers
// - if the transaction became NON SPLIT (was split, now it is not) all prior transfers are deleted and then
//   if applicable, new transfer transaction created (if non split version is a transfer) - with warning
//
interface preprocessUpdateProps {
  txns: TransactionRecord[];
  txnsByAccountId: accTxnMap;
  featureFlags: any;
  state?: RootState;
  txnOverrides?: Partial<TransactionProps>;
}
export function preProcessUpdate({
  txns,
  txnsByAccountId,
  featureFlags,
  state = store!.getState(), // TODO: We should require everyone to pass in state.
  txnOverrides,
}: preprocessUpdateProps) {
  const futureTxnsFf = featureFlags.get('futureTxns');
  const excludeIncomeByDefault = preferencesV2Selectors.getPreferenceValue(state, 'sSPExcludeIncomeByDefault', false);
  const incomeCoaIds = categoriesSelectors.getIncomeCOAIds(state);
  const businessCoaIds = categoriesSelectors.getBusinessCoaIdsSet(state);

  const allTxnsToDelete: TransactionRecord[] = []; // transfer transactions could be eliminated
  const allTxnsToUpdate: TransactionRecord[] = []; // all transactions might change
  const allTxnsToCreate: TransactionRecord[] = []; // new transfer transactions may be created
  const allMatches: MatchObject[] = [];
  const warnings: string[] = [];
  const errors: string[] = [];
  const guides: Guide[] = [];
  const extraActions: any[] = [];

  function localMergeTxnPack(packItems: Partial<TxnPack>) {
    mergeTxnPack(packItems, {
      allTxnsToUpdate,
      allTxnsToDelete,
      allTxnsToCreate,
      allMatches,
      warnings,
      errors,
      extraActions,
      guides,
    });
  }

  // Process every transaction in the update
  txns.forEach((x) => {
    // general formatting

    let txn = x;
    let txnPrev = null;
    let isNewTxn = !isExistingTxn(txn);

    let errorThrown = false; // if an error is thrown, we do not add any entries to the update/create/delete

    // make sure there is a legit amount
    txn = txn.set('amount', Number(txn.amount || 0));

    // convert date to an ISO String
    txn = txn.set('postedOn', DateTime.fromISO(txn.postedOn).toISODate() || DateTime.now().toISODate());

    // if this is a CREATE (new) Transaction, add a clientId if there is not one already
    if (isNewTxn && !txn.clientId) {
      txn = txn.set('clientId', uuidv4().toUpperCase());
    }

    //    Split logic
    // - - - - - - - - - - - - - - - - - - -- - - - - - - - -
    if (isSplitTxn(txn)) {
      assert(!txn.coa, 'You are saving a transaction with a COA and a SPLIT - Please tell CHRIS what you did');
      // clear coa and tags if there are any
      if (txn.type !== 'INVOICE') {
        txn = txn.merge({ businessId: '', customerId: '', projectId: '', tags: List(), coa: null });
      }
      // enforce immutable
      txn = txn.set('split', transactionsTypes.mkSplit(txn.split!));

      // remove any zero items for non-invoice transactions.
      // Invoice split lines can have amount 0 and should be retained.
      if (txn.type !== 'INVOICE' && txn.split?.items) {
        txn = txn.setIn(
          ['split', 'items'],
          (txn.split.items as SplitItem[]).filter((item: SplitItem) => Number(item.amount) !== 0),
        );
      }

      //
      // ensure the transaction balances
      //
      const remainder = splitsHelpers.splitTxnRemainder(txn);
      if (remainder !== 0.0) {
        txn = splitsHelpers.addRemainderToSplit(txn, remainder);
      }

      // if only one split item left, convert it to a normal category (unless txn type is an invoice)
      const items = txn.split?.items as Immutable.List<Immutable.RecordOf<transactionsTypes.SplitItemProps>>;
      if (items?.size === 1 && txn.type !== 'INVOICE') {
        // only one split item
        const currentItem = items.first() as transactionsTypes.SplitItemProps;
        txn = txn.merge({
          coa: currentItem.coa,
          businessId: currentItem.businessId,
          customerId: currentItem.customerId,
          projectId: currentItem.projectId,
          tags: currentItem.tags,
          split: null,
        });
      } else if (txn.split?.items && 'set' in txn.split.items) {
        // genuine split with multiple items

        let removedSelfTransfer = false;

        // make sure amounts are numbers
        // also assign clientId's to any split items that do not have any
        txn = txn.setIn(
          ['split', 'items'],
          txn.split.items.map((item) => {
            let newItem = item?.set('amount', Number(item.amount));
            // prevent self-transfers
            if (newItem.coa?.type === 'ACCOUNT' && newItem.coa.id === txn.accountId) {
              removedSelfTransfer = true;
              newItem = newItem.set('coa', { type: 'UNCATEGORIZED', id: '0' });
            }

            if (item?.id) {
              return newItem;
            }
            return newItem?.set('clientId', uuidv4().toUpperCase());
          }),
        );

        if (removedSelfTransfer) {
          warnings.push(
            'You cannot create a transaction as a self-transfer. Your transfer split item will be saved as uncategorized.',
          );
        }
      }
    } else if (txn.coa && txn.coa.type === 'ACCOUNT' && txn.accountId === txn.coa.id) {
      // prevent self-transfers from being created
      warnings.push(
        'You cannot create a transaction as a self-transfer. Your transaction will be saved as uncategorized.',
      );
      txn = txn.set('coa', { type: 'UNCATEGORIZED', id: '0' });
    }

    // If this transaction has tags, check for any tags with id 0, and either find an actual tag
    // with that 'name' and use its ID, or remove it

    txn = updateNewTags(txn, true, state);
    if (!txn.accountId) return;

    const accountExcludedFromBudgets = Boolean(
      accountsSelectors.getAccountById(state, txn.accountId)?.isExcludedFromBudgets,
    );

    let dateChanged = false;
    let accountChanged = false;
    let coaChanged = false;
    let businessIdChanged = false;
    let customerIdChanged = false;
    let projectIdChanged = false;

    const hideConnectedLoanTransactions = featureFlags.get('hideConnectedLoanTransactions');

    if (accountsUtils.transactionsNotSupportedForAccountId(txn.accountId, hideConnectedLoanTransactions, state)) {
      errors.push('This account does not support creating or modifying transactions.');
      errorThrown = true;
    }

    // do not allow transfers to any account that does not support them
    if (txnHasUnsupportedTransfer(txn, featureFlags, state)) {
      errors.push('This transaction contains a transfer to an account that does not support transactions.');
      errorThrown = true;
    }

    // get the current stored transaction by id to identify changes in the update
    if (!isNewTxn && txn.id) {
      txnPrev = transactionsUtils.isRefundTxn(txn) ? txn : findTransaction(txnsByAccountId, txn.id, txn.accountId);

      if (!txnPrev) {
        log.log('Attempting an update after txn was deleted, treat this as a create for a newTxn');
        isNewTxn = true;
        txn = txn.set('id', null).set('clientId', uuidv4().toUpperCase());
      } else {
        // Txn update (field change) logic

        dateChanged = fieldChanged(txn, txnPrev, 'postedOn');
        accountChanged = txn.accountId !== txnPrev.accountId;
        coaChanged = fieldChanged(txn, txnPrev, 'coa');
        businessIdChanged = fieldChanged(txn, txnPrev, 'businessId');
        customerIdChanged = fieldChanged(txn, txnPrev, 'customerId');
        projectIdChanged = fieldChanged(txn, txnPrev, 'projectId');
        if (txnPrev.split && !txn.split && txn.coa) {
          txn = txn.set('split', transactionsTypes.mkSplit({ items: [] }));
        }

        if (txnPrev.memo && txnPrev.memo.length > 0 && !txn.memo) {
          txn = txn.set('memo', '');
        }

        // do not allow them to change a cleared/reconciled bank downloaded transaction to pending
        if (!isPendingTxn(txnPrev) && isPendingTxn(txn) && isBankDownloadedTxn(txn)) {
          errors.push("Sorry, but you cannot change a cleared bank downloaded transaction to 'Pending'");
          errorThrown = true;
        }
      }
    }

    if (futureTxnsFf) {
      const today = dateSelectors.getCurrentDateString(state ?? (store?.getState() as RootState));
      if ((txnPrev?.get?.('postedOn') || today) <= today && txn.get('postedOn') > today) {
        guides.push(Guide.FUTURE_TXN_CREATED);
      }
    }

    // should not be possible, but do not allow creation of transactions to unsynced accounts
    if (isNewTxn && isUnsyncedTransferTxn(txn, state) && errors.length === 0) {
      errors.push('You cannot save a transaction with a transfer to an account which has not been synced');
      errorThrown = true;
    }

    // If this is a transfer transaction, and the currency types of the accounts are different, do not
    // allow to create or edit
    //
    if (isMultiCurrencyTransferTxn(txn, state)) {
      errors.push(
        'Multi currency transfers: ' +
          'We are sorry, but creating or editing multi currency transfers is not supported.',
      );
      errorThrown = true;
    }

    // After amount is finally determined, check if it changed for an existing transaction
    const amountChanged = isNewTxn ? false : Number(txn.amount) !== Number(txnPrev?.amount);

    /* *******************************************************************************************
     * Begin Processing the transaction, below are various cases for processing the update/create
     * Start by ensuring there are not validation errors with the fields
     * If an error was thrown above, we just bail out and do not do the subsequent processing/checks
     */

    // convert the amount(s) to numbers
    txn = txn.set('amount', Number(txn.amount || 0));
    if (isSplitTxn(txn) && txn.split?.items && 'set' in txn.split && 'set' in txn.split.items) {
      const newItems = txn.split.items.map((item) => item?.set('amount', Number(item.amount)));

      txn.set('split', txn.split.set('items', newItems));
    }

    // if there is no split, and no coa, make it uncategorized
    if (!isSplitTxn(txn) && (!txn.coa || !txn.coa.type)) {
      txn = txn.set('coa', chartOfAccountsTypes.mkChartOfAccount({ type: 'UNCATEGORIZED', id: '0' }));
    }

    const validateError = validateTxn(txn);

    if ((!txn.isDeleted && validateError) || errorThrown) {
      if (!errorThrown) {
        errors.push(`Cannot ${isNewTxn ? 'create' : 'update'} transaction ${txn.payee || ''}: ${validateError}`);
        errorThrown = true;
      }

      /*
       * Do not allow changing the account of a downloaded transaction
       */
    } else if ((txn.cpData || isTransferTxn(txn)) && accountChanged) {
      errors.push(`You cannot change the account of a ${txn.cpData ? 'downloaded' : 'transfer'} transaction`);
      errorThrown = true;

      /*
       * NO TRANSFERS in the previous transaction, proceed with update/create
       * Note, the new transaction may have transfers, so we create those
       * THIS IS THE MOST COMMON CASE, previous transaction is being updated and had no transfers
       * OR IF txnPrev is null, there is no previous transaction (this is a create)
       */
    } else if (!txnPrev || !isTransferTxn(txnPrev)) {
      const setFlag = coaChanged;
      // if category changed to transfer type, we set ignore from reports to true
      if (setFlag) {
        if (chartOfAccountsUtils.coaIsBalanceAdjustmentType(txn.coa) || isTransferTxn(txn)) {
          // is transfer or adjustment now
          txn = setReportsFlags(txn, true);
        } else if (txnPrev && chartOfAccountsUtils.coaIsBalanceAdjustmentType(txnPrev.coa)) {
          // isn't transfer or adjustment, but used to be, so clear the flag
          txn = setReportsFlags(txn, false);
        }
      }

      if (coaChanged) {
        const nextAutoExcluded = isAutoExcludedFromF2S(txn.coa, excludeIncomeByDefault, incomeCoaIds, businessCoaIds);
        const prevAutoExcluded = isAutoExcludedFromF2S(
          txnPrev?.coa,
          excludeIncomeByDefault,
          incomeCoaIds,
          businessCoaIds,
        );

        // Only preserve a user's explicit spending-plan inclusion when transitioning between
        // two income COAs specifically.  Business categories are never user-configurable for
        // spending-plan inclusion, so any transition involving a business COA (business→business,
        // income→business, business→income) should always force-exclude.
        const prevIsIncomeCoa = excludeIncomeByDefault && Boolean(incomeCoaIds?.has?.(txnPrev?.coa?.id ?? ''));
        const nextIsIncomeCoa = excludeIncomeByDefault && Boolean(incomeCoaIds?.has?.(txn.coa?.id ?? ''));
        const isIncomeToIncomeTransition = prevIsIncomeCoa && nextIsIncomeCoa;

        if (nextAutoExcluded && !isIncomeToIncomeTransition) {
          // Entering auto-excluded territory, or changing between non-income auto-excluded
          txn = txn.set('isExcludedFromF2S', true);
        } else if (!nextAutoExcluded && prevAutoExcluded) {
          // Leaving auto-excluded territory (e.g. income → shopping): clear the flag.
          txn = txn.set('isExcludedFromF2S', accountExcludedFromBudgets ? true : false);
        }
        // income → income (isIncomeToIncomeTransition): neither branch fires, leaving
        // isExcludedFromF2S untouched so a user's explicit inclusion choice is preserved.
      }

      // apply any explicit overrides to txn - this is used in the case of transaction details dialog where the user can
      // manually specify exclude flags which should override the logic immediately above
      txn = txnOverrides ? txn.mergeDeep(txnOverrides) : txn;

      const txnPack = createRequiredTransferTxns(txn, txnsByAccountId, featureFlags, state, undefined, txns);
      localMergeTxnPack(txnPack);

      /*
       * REMAINING CASE ARE FOR PROCESSING THE PRIOR TRANSFER(S) IN THE PREVIOUS TRANSACTION
       */
    } else {
      // retrieve txn candidates for matching transfer items in the split
      const oldDestinationTransfers = getTransferCandidates(txnPrev, txnsByAccountId);

      // if any of the oldDestinationTransfers are also in the transactions to be updated, and we have changed
      // the coa of this transaction, don't allow it
      // TODO Unit Tests for this
      if (
        coaChanged &&
        oldDestinationTransfers.find(({ txn: destTxn }) =>
          allTxnsToUpdate.find((updateTxn) => updateTxn.id === destTxn.id),
        )
      ) {
        errors.push('Really sorry, but you cannot change the category of both sides of a transfer at the same time');
        errorThrown = true;
      }

      // if the current transaction is no longer a transfer, unset the report flags as well
      if (!isTransferTxn(txn) && !chartOfAccountsUtils.coaIsBalanceAdjustmentType(txn.coa)) {
        txn = setReportsFlags(txn, false);
      }

      if (coaChanged) {
        const nextAutoExcluded = isAutoExcludedFromF2S(txn.coa, excludeIncomeByDefault, incomeCoaIds, businessCoaIds);
        const prevAutoExcluded = isAutoExcludedFromF2S(
          txnPrev?.coa,
          excludeIncomeByDefault,
          incomeCoaIds,
          businessCoaIds,
        );

        if (nextAutoExcluded) {
          txn = txn.set('isExcludedFromF2S', true);
        } else if (prevAutoExcluded) {
          txn = txn.set('isExcludedFromF2S', accountExcludedFromBudgets ? true : false);
        }
      }

      // apply any explicit overrides to txn - this is used in the case of transaction details dialog where the user can
      // manually specify exclude flags which should override the logic immediately above
      txn = txnOverrides ? txn.mergeDeep(txnOverrides) : txn;

      // `accountChanged` is omitted — already errored above with its own message.
      const scheduledSplitCounterpart = txnPrev && isScheduledSplitTransferCounterpart(txnPrev, state);
      const scheduledSplitGuardedFieldsChanged =
        amountChanged || dateChanged || coaChanged || businessIdChanged || customerIdChanged || projectIdChanged;

      if (scheduledSplitCounterpart && scheduledSplitGuardedFieldsChanged) {
        errors.push(
          `Cannot update transaction "${txn.payee}", the other side of the transfer is in a split.` +
            ' To update it, edit the transfer transaction in the transfer account',
        );
        errorThrown = true;
      }

      // create or update other sides of the transfer txn
      const txnPack = scheduledSplitCounterpart
        ? {
            allTxnsToCreate: [],
            allTxnsToUpdate: [
              txn.merge({
                stModelId: txnPrev.stModelId,
                transfer: txnPrev.transfer,
              }),
            ],
            allTxnsToDelete: [],
            warnings: [],
            allMatches: [],
            transfersToPersist: [] as TransactionRecord[],
            extraActions: [],
          }
        : createRequiredTransferTxns(txn, txnsByAccountId, featureFlags, state, txnPrev, txns);

      const transfersToDelete: TransactionRecord[] = [];
      const transfersToUpdate = txnPack.allTxnsToUpdate;

      // for each old transfer endpoint, we see if it exists in txnsToUpdate
      // or transfersToPersist. If not, we delete it
      oldDestinationTransfers.forEach((oldTransferPoint) => {
        /*
         * if the destination endpoint is in a split, we cannot edit shared fields
         */
        if (oldTransferPoint.splitItem !== null) {
          // Skip to avoid duplicating the error pushed by the scheduled-split guard above.
          if (scheduledSplitCounterpart) return;
          // TODO: perhaps in the future allow the user to update shared fields and just make sure we apply them correctly?
          const splitTransferComparison = getConnectedSplitTransferSourceComparison({
            txn,
            txnPrev,
            oldTransferPoint,
            amountChanged,
            dateChanged,
            coaChanged,
            businessIdChanged,
            customerIdChanged,
            projectIdChanged,
          });
          const splitSourceTxnIsAlreadyStaged = Boolean(
            allTxnsToUpdate.find((updateTxn) => updateTxn.id === oldTransferPoint.txn.id),
          );
          const priorStagedTxnUpdate = allTxnsToUpdate.find((updateTxn) => updateTxn.id === txn.id);
          const stagedSplitDrivenUpdateMatchesCurrentTxn =
            splitSourceTxnIsAlreadyStaged && doesTxnMatchGuardedTransferFields(priorStagedTxnUpdate, txn);
          if (splitTransferComparison.sharedTransferFieldsChanged && !stagedSplitDrivenUpdateMatchesCurrentTxn) {
            errors.push(
              `Cannot update transaction "${txn.payee}", the other side of the transfer is in a split.` +
                ' To update it, edit the transfer transaction in the transfer account',
            );
            errorThrown = true;
          } else {
            log.log('simple connected splitItem transfer edit - no edits needed to source txn');
          }

          /*
           * Destination endpoint is not in a split, so we can make adjustments to transfers
           */
        } else {
          const oldTransfer = oldTransferPoint.txn;

          if (
            txnPack.allTxnsToUpdate.find((tx) => tx.id === oldTransfer.id) || // updating txn
            txnPack.transfersToPersist.find((tx) => tx.id === oldTransfer.id)
          ) {
            // keeping txn
            // keep endpoints
          } else if (oldTransfer.cpData) {
            // if we got to this point, the oldTransferPoint is meant to be removed
            // if the endpoint is a bank downloaded transaction, un-categorize it, and unset reports exclusion
            log.log('Removed one side of transfer', '\nUncategorizing other side');
            const uncatFields = resolveTransferBusinessFields(
              oldTransfer.accountId || '',
              oldTransfer.businessId,
              oldTransfer.customerId,
              oldTransfer.projectId,
              state,
            );
            transfersToUpdate.push(
              setReportsFlags(
                oldTransfer.merge({
                  coa: { type: 'UNCATEGORIZED', id: '0' },
                  transfer: null,
                  ...uncatFields,
                }),
                false,
              ),
            );
          } else {
            // endpoint is manual txn
            // we delete the old endpoint (no longer a reference to it in the base txn)
            transfersToDelete.push(oldTransfer.set('isDeleted', true));
            if (!isQBPCreditTxn(state, { txn: oldTransfer })) {
              warnings.push(
                `You are editing the transfer transaction "${txn.payee}" to no longer be a transfer. ` +
                  `This will also delete the other side of the transfer in account [${accountsUtils.getAccountString(
                    oldTransfer.accountId,
                    state,
                  )}] `,
              );
            }
          }
        }
      });

      if (!errorThrown) {
        localMergeTxnPack({
          ...txnPack,
          allTxnsToUpdate: transfersToUpdate,
          allTxnsToDelete: transfersToDelete,
        });
      }
    }
  }); // end - for each txn

  // if we are creating txns, warn the user if any of them would be in a closed account
  if (allTxnsToCreate.find((txn) => accountsSelectors.getAccountById(state, txn.accountId)?.isClosed)) {
    warnings.push('This will create a transaction in a closed account, are you sure you want to proceed?');
  }

  // in some cases during a multi-transaction update, a transfer transaction might be getting updated
  // along with one of its children.  This can result in a new child transaction being created, and the
  // previous transaction being deleted, but also the previous transaction may attempt to update itself
  // effectively undeleting itself.  We check for any updates conflicting with deletes, and we remove
  // the updates if there is a conflict (delete wins)
  //
  const txnPack = {
    allMatches,
    allTxnsToDelete,
    allTxnsToUpdate,
    allTxnsToCreate,
    warnings,
    errors,
    guides: guides.filter(guideFilter(state)),
    extraActions,
  };
  return removeConflicts(txnPack);
}

function removeConflicts(txnPack: TxnPack) {
  const { allTxnsToDelete, allTxnsToUpdate, allTxnsToCreate } = txnPack;

  const mergedUpdates = allTxnsToUpdate
    // We do not want to update items that are on track for deletion
    .filter((txn) => !allTxnsToDelete.find((x) => x.id === txn.id))
    // We don't want to update the same record twice, so instead we will consolidate into one update
    .reduce<typeof allTxnsToUpdate>((newUpdates, update) => {
      const existingIndex = newUpdates.findIndex((txn) => isTxnMatch(txn, update));
      if (existingIndex !== -1) {
        newUpdates[existingIndex] = mergeTxns(newUpdates[existingIndex], update);
      } else {
        newUpdates.push(update);
      }
      return newUpdates;
    }, []);

  // If there are new transactions that are due for an update we want to consolidate that
  // A case where this can happen is if you are creating the "other side" of a transfer but also editing the source side
  // This will propogate updates to a transaction that hasn't been created yet.
  const updatedCreates = allTxnsToCreate.map((newTxn) => {
    const existingIndex = mergedUpdates.findIndex((txn) => isTxnMatch(txn, newTxn));
    if (existingIndex !== -1) {
      return mergeTxns(newTxn, mergedUpdates[existingIndex]);
    }
    return newTxn;
  });

  const updatesWithoutCreate = mergedUpdates.filter((txn) => !!txn.id);

  return { ...txnPack, allTxnsToUpdate: updatesWithoutCreate, allTxnsToCreate: updatedCreates };
}

type TransferCandidate = {
  txn: TransactionRecord;
  splitItem: number | null;
  sourceSplitItemId?: string | null;
  sourceSplitItemClientId?: string | null;
};

function findTransferCandidateSourceSplitItem(txn: TransactionRecord, transferCandidate: TransferCandidate) {
  const items = txn.split?.items;

  if (!items || (!transferCandidate.sourceSplitItemId && !transferCandidate.sourceSplitItemClientId)) {
    return undefined;
  }

  return items.find(
    (item) =>
      (!!transferCandidate.sourceSplitItemId && item.id === transferCandidate.sourceSplitItemId) ||
      (!!transferCandidate.sourceSplitItemClientId && item.clientId === transferCandidate.sourceSplitItemClientId),
  );
}

function doesTxnMatchGuardedTransferFields(
  stagedTxn: Pick<TransactionProps, splitsHelpers.SplitTransferGuardedField> | null | undefined,
  txn: Pick<TransactionProps, splitsHelpers.SplitTransferGuardedField>,
): boolean {
  if (!stagedTxn) {
    return false;
  }

  return (
    Number(stagedTxn.amount ?? 0) === Number(txn.amount ?? 0) &&
    (stagedTxn.postedOn ?? '') === (txn.postedOn ?? '') &&
    !coasAreDifferent(stagedTxn.coa, txn.coa) &&
    (stagedTxn.businessId ?? '') === (txn.businessId ?? '') &&
    (stagedTxn.customerId ?? '') === (txn.customerId ?? '') &&
    (stagedTxn.projectId ?? '') === (txn.projectId ?? '')
  );
}

function getConnectedSplitTransferSourceComparison({
  txn,
  txnPrev,
  oldTransferPoint,
  amountChanged,
  dateChanged,
  coaChanged,
  businessIdChanged,
  customerIdChanged,
  projectIdChanged,
}: {
  txn: TransactionRecord;
  txnPrev: TransactionRecord;
  oldTransferPoint: TransferCandidate;
  amountChanged: boolean;
  dateChanged: boolean;
  coaChanged: boolean;
  businessIdChanged: boolean;
  customerIdChanged: boolean;
  projectIdChanged: boolean;
}) {
  const requestedChangeFlags = {
    amountChanged,
    dateChanged,
    coaChanged,
    businessIdChanged,
    customerIdChanged,
    projectIdChanged,
  };

  if (dateChanged) {
    return {
      prevSourceItem: undefined,
      nextSourceItem: undefined,
      splitSourceFieldChanges: undefined,
      requestedChangeFlags,
      usingSplitSourceComparison: false,
      sharedTransferFieldsChanged: true,
    };
  }

  if (isSplitTxn(txnPrev) && isSplitTxn(txn)) {
    const prevSourceItem = findTransferCandidateSourceSplitItem(txnPrev, oldTransferPoint);
    const nextSourceItem = findTransferCandidateSourceSplitItem(txn, oldTransferPoint);

    if (!prevSourceItem || !nextSourceItem) {
      return {
        prevSourceItem,
        nextSourceItem,
        splitSourceFieldChanges: undefined,
        requestedChangeFlags,
        usingSplitSourceComparison: true,
        sharedTransferFieldsChanged: true,
      };
    }

    const splitSourceFieldChanges = {
      amountChanged: Number(prevSourceItem.amount) !== Number(nextSourceItem.amount),
      coaChanged: coasAreDifferent(prevSourceItem.coa, nextSourceItem.coa),
      businessIdChanged: (prevSourceItem.businessId ?? '') !== (nextSourceItem.businessId ?? ''),
      customerIdChanged: (prevSourceItem.customerId ?? '') !== (nextSourceItem.customerId ?? ''),
      projectIdChanged: (prevSourceItem.projectId ?? '') !== (nextSourceItem.projectId ?? ''),
    };

    return {
      prevSourceItem,
      nextSourceItem,
      splitSourceFieldChanges,
      requestedChangeFlags,
      usingSplitSourceComparison: true,
      sharedTransferFieldsChanged:
        splitSourceFieldChanges.amountChanged ||
        splitSourceFieldChanges.coaChanged ||
        splitSourceFieldChanges.businessIdChanged ||
        splitSourceFieldChanges.customerIdChanged ||
        splitSourceFieldChanges.projectIdChanged,
    };
  }

  return {
    prevSourceItem: undefined,
    nextSourceItem: undefined,
    splitSourceFieldChanges: undefined,
    requestedChangeFlags,
    usingSplitSourceComparison: false,
    sharedTransferFieldsChanged:
      amountChanged || coaChanged || businessIdChanged || customerIdChanged || projectIdChanged,
  };
}

function getTransferCandidates(txnPrev: TransactionRecord, txnsByAccountId: accTxnMap): TransferCandidate[] {
  const oldDestinationTransfers: TransferCandidate[] = [];
  if (isSplitTxn(txnPrev) && txnPrev?.split?.items) {
    txnPrev.split.items.forEach((item) => {
      if (item.coa?.type === 'ACCOUNT') {
        const transferTxns =
          txnsByAccountId.get(item.coa.id)?.filter((tx) => isTransferTxn(tx)) ||
          ImmutableMap<string, TransactionRecord>();
        const oldTxn = findMatchingTransferTx(txnPrev, transferTxns, item.amount, item.transfer);
        oldTxn &&
          oldDestinationTransfers.push({
            ...oldTxn,
            sourceSplitItemId: item.id,
            sourceSplitItemClientId: item.clientId,
          });
      }
    });
  } else if (txnPrev.coa?.type === 'ACCOUNT') {
    const transferTxns =
      txnsByAccountId.get(txnPrev.coa.id)?.filter((tx) => isTransferTxn(tx)) ||
      ImmutableMap<string, TransactionRecord>();
    const oldTxn = findMatchingTransferTx(txnPrev, transferTxns);
    oldTxn && oldDestinationTransfers.push(oldTxn);
  }
  return oldDestinationTransfers;
}

export const addMatches = (txnPack: Pick<TxnPack, 'allMatches' | 'allTxnsToUpdate' | 'allTxnsToCreate'>) => {
  const { allMatches, allTxnsToUpdate, allTxnsToCreate } = txnPack;

  // Go through all matches and replace the transaction to create or update with the matching txn
  const success = allMatches.every((match) => {
    const { possibleMatch, baseTxn } = match;
    if (possibleMatch) {
      if (isExistingTxn(possibleMatch)) {
        allTxnsToUpdate.push(possibleMatch);
        // txn = txn.set('coa', { type: 'ACCOUNT', id: bestMatch.accountId });
      } else {
        // for some reason, the possible match is currently being created
        // hopefully this shouldn't happen
        const existingIndex = allTxnsToCreate.findIndex((txn) => txn.clientId === possibleMatch?.clientId);
        if (existingIndex > -1) {
          allTxnsToCreate.splice(existingIndex, 1, possibleMatch);
        }
      }
      // after adding new match, update the other side with the new connected transfer coa
      return replaceInArray(allTxnsToUpdate, baseTxn) || replaceInArray(allTxnsToCreate, baseTxn);
      // if cannot find base txn to match with, returns false
      // SHOULD NOT HAPPEN - false will display warning dialog
      // TODO: alternatively, we could just update/create if we can't already find baseTxn already staged
      //  not sure if that's what we want to do, but an option if users do see "Error: Matching operation failed"
    }

    return true;
  });

  return success;
};

//-------------------------------------------------------
// validateTxn
//
function validateTxn(txn: TransactionProps) {
  if (!txn.postedOn) {
    return 'Must have a valid date';
  }
  if (!isSupportedTransactionAmount(txn.amount)) {
    return `Whoops! Really sorry, but that's a bigger number than we support for a single transaction. ${transactionsUtils.UNSUPPORTED_TRANSACTION_AMOUNT_HELPER_TEXT}`;
  }
  if (!isSplitTxn(txn) && (!txn.coa || !txn.coa.type)) {
    return 'Please enter a Category/Transfer for this transaction';
  }
  return null;
}
